import React, { FC, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import {
  Box,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { Download, Trash2, Upload } from "feather-icons-react";
import { useTranslation } from "react-i18next";
import { FixedSizeList, ListChildComponentProps } from "react-window";

import { parseFileToRows } from "../../../../../../hooks/useFileParser";

import { Button } from "@components/base";

const ROW_HEIGHT = 64;
const MAX_LIST_HEIGHT = 400;
const HEADER_GRID_TEMPLATE = "70px 1.5fr 2fr 80px 44px";
const CELL_PADDING = 10;
const GRID_GAP = 2;

// Common styles for all input fields
const fieldSx = {
  "& .MuiFilledInput-root": {
    py: 0,
    minHeight: 38,
  },
  "& .MuiFilledInput-input": {
    py: "10px",
    px: "12px",
    fontSize: "0.85rem",
  },
};

export interface PositionListItem {
  id: string;
  jobTitle: string;
  similarJobTitles: string;
  grade: number;
}

interface RowData {
  positionList: PositionListItem[];
  onDeletePosition: (id: string) => void;
  onUpdatePosition: (id: string, field: keyof PositionListItem, value: string | number) => void;
}

const Row: FC<ListChildComponentProps<RowData>> = ({ index, style, data }) => {
  const { positionList, onDeletePosition, onUpdatePosition } = data;
  const position = positionList[index];

  return (
    <Box
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        borderTop: "1px solid",
        borderColor: "grey.100",
        backgroundColor: index % 2 === 0 ? "white" : "grey.50",
        boxSizing: "border-box",
        px: 2,
      }}
    >
      <Box
        display="grid"
        gridTemplateColumns={HEADER_GRID_TEMPLATE}
        alignItems="center"
        gap={GRID_GAP}
        width="100%"
        sx={{ fontSize: "0.85rem" }}
      >
      <TextField
        size="small"
        variant="filled"
        value={position.id}
        onChange={(e): void => onUpdatePosition(position.id, "id", e.target.value)}
        sx={fieldSx}
      />
      <TextField
        size="small"
        variant="filled"
        value={position.jobTitle}
        onChange={(e): void => onUpdatePosition(position.id, "jobTitle", e.target.value)}
        sx={fieldSx}
      />
      <TextField
        size="small"
        variant="filled"
        value={position.similarJobTitles}
        onChange={(e): void => onUpdatePosition(position.id, "similarJobTitles", e.target.value)}
        sx={fieldSx}
      />
      <TextField
        size="small"
        variant="filled"
        type="number"
        value={position.grade}
        onChange={(e): void => {
          const val = e.target.value === "" ? 0 : Number(e.target.value);

          onUpdatePosition(position.id, "grade", val);
        }}
        sx={fieldSx}
      />
      <Box display="flex" justifyContent="center">
        <IconButton size="small" onClick={(): void => onDeletePosition(position.id)}>
          <Trash2 size={14} />
        </IconButton>
      </Box>
      </Box>
    </Box>
  );
};

Row.displayName = "PositionListRow";

interface PositionListTabProps {
  positionList: PositionListItem[];
  setPositionList: React.Dispatch<React.SetStateAction<PositionListItem[]>>;
  onSavePositionList: (list: PositionListItem[]) => void;
  hideSaveButton?: boolean;
}

export const PositionListTab: FC<PositionListTabProps> = ({
  positionList,
  setPositionList,
  onSavePositionList,
  hideSaveButton = false,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
      const file = event.target.files?.[0];

      if (!file) return;

      try {
        const rows = await parseFileToRows(file);

        // Skip header row, parse data
        const parsed: PositionListItem[] = [];

        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];

          if (row.length < 4) continue;

          const [id, jobTitle, similarJobTitles, gradeStr] = row;
          const grade = Number(gradeStr);

          if (!id || !jobTitle || Number.isNaN(grade)) continue;

          parsed.push({
            id: id.trim(),
            jobTitle: jobTitle.trim(),
            similarJobTitles: (similarJobTitles || "").trim(),
            grade,
          });
        }

        setPositionList(parsed);
        onSavePositionList(parsed);
      } catch (error) {
        console.error("Error parsing position list file:", error);
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [setPositionList, onSavePositionList],
  );

  const handleDownloadCsvTemplate = useCallback((): void => {
    const csvContent = `ID,Job title,Similar job titles,Grade
1,Software Engineer,"Developer, Programmer",5
2,Senior Developer,"Lead Developer, Tech Lead",7
3,Manager,"Team Lead, Supervisor",8`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", "position_list_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const handleDownloadExcelTemplate = useCallback((): void => {
    const headers = ["ID", "Job title", "Similar job titles", "Grade"];
    const sampleRows = [
      ["1", "Software Engineer", "Developer, Programmer", "5"],
      ["2", "Senior Developer", "Lead Developer, Tech Lead", "7"],
      ["3", "Manager", "Team Lead, Supervisor", "8"],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Position List");
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.download = "position_list_template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const handleDeletePosition = useCallback(
    (id: string): void => {
      const updated = positionList.filter((p) => p.id !== id);

      setPositionList(updated);
      onSavePositionList(updated);
    },
    [positionList, setPositionList, onSavePositionList],
  );

  const handleUpdatePosition = useCallback(
    (id: string, field: keyof PositionListItem, value: string | number): void => {
      const updated = positionList.map((p) =>
        p.id === id ? { ...p, [field]: value } : p,
      );

      setPositionList(updated);
    },
    [positionList, setPositionList],
  );

  const handleClearAll = useCallback((): void => {
    setPositionList([]);
    onSavePositionList([]);
  }, [setPositionList, onSavePositionList]);

  const handleSave = useCallback((): void => {
    onSavePositionList(positionList);
  }, [positionList, onSavePositionList]);

  const itemData: RowData = {
    positionList,
    onDeletePosition: handleDeletePosition,
    onUpdatePosition: handleUpdatePosition,
  };

  const listHeight = Math.min(positionList.length * ROW_HEIGHT, MAX_LIST_HEIGHT);

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv, .xlsx, .xls"
          onChange={handleFileUpload}
          style={{ display: "none" }}
          id="position-list-upload"
        />
        <label htmlFor="position-list-upload">
          <Button
            variant="outlined"
            component="span"
            startIcon={<Upload size={16} />}
            size="small"
          >
            {t("paygap.gradeEstimator.uploadPositionList", "Upload position list")}
          </Button>
        </label>
        <Tooltip title={t("paygap.gradeEstimator.downloadCsvTemplate", "Download CSV template")}>
          <Button
            variant="outlined"
            startIcon={<Download size={16} />}
            onClick={handleDownloadCsvTemplate}
            size="small"
          >
            {"CSV"}
          </Button>
        </Tooltip>
        <Tooltip
          title={t("paygap.gradeEstimator.downloadExcelTemplate", "Download Excel template")}
        >
          <Button
            variant="outlined"
            startIcon={<Download size={16} />}
            onClick={handleDownloadExcelTemplate}
            size="small"
          >
            {"Excel"}
          </Button>
        </Tooltip>
        {positionList.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<Trash2 size={16} />}
            onClick={handleClearAll}
            size="small"
          >
            {t("paygap.gradeEstimator.clearAll", "Clear all")}
          </Button>
        )}
      </Stack>

      <Divider />

      <Box
        sx={{
          border: "1px solid",
          borderColor: "grey.200",
          borderRadius: 1,
        }}
      >
        <Box
          display="grid"
          gridTemplateColumns={HEADER_GRID_TEMPLATE}
          alignItems="center"
          bgcolor="grey.100"
          gap={GRID_GAP}
          px={2}
          py={1.25}
          fontWeight={600}
          fontSize="0.85rem"
          sx={{
            "& > *": { whiteSpace: "normal", px: `${CELL_PADDING}px` },
          }}
        >
          <Box>{t("paygap.gradeEstimator.positionListColumns.id", "ID")}</Box>
          <Box>{t("paygap.gradeEstimator.positionListColumns.jobTitle", "Job title")}</Box>
          <Box>
            {t("paygap.gradeEstimator.positionListColumns.similarJobTitles", "Similar job titles")}
          </Box>
          <Box>{t("paygap.gradeEstimator.positionListColumns.grade", "Grade")}</Box>
          <Box />
        </Box>
        {positionList.length === 0 ? (
          <Box px={1} py={2} fontSize="0.9rem" color="text.secondary" textAlign="center">
            {t("paygap.gradeEstimator.noPositionList", "No positions uploaded yet")}
          </Box>
        ) : (
          <FixedSizeList
            height={listHeight}
            itemCount={positionList.length}
            itemSize={ROW_HEIGHT}
            width="100%"
            itemData={itemData}
          >
            {Row}
          </FixedSizeList>
        )}
      </Box>

      {positionList.length > 0 && !hideSaveButton && (
        <Stack direction="row" justifyContent="flex-end" spacing={1} mt={1}>
          <Button variant="contained" color="primary" onClick={handleSave}>
            {t("paygap.gradeEstimator.savePositionList", "Save position list")}
          </Button>
        </Stack>
      )}
    </Box>
  );
};
